import React, { useState, useEffect, useRef, useCallback } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, initials, today, uuid, invoiceStatus, dateInRange, logAudit, notify, resolveInvoiceFinancials } from '../../core/helpers';
import { sb } from '../../core/db';
import { TASK_STATUSES, TASK_REQUIREMENT_TYPES, DEFAULT_DOC_TEMPLATES } from '../../core/constants';
import { Badge } from '../../components/Badge';
import { FilterBar } from '../../components/FilterBar';
import { StatusFilter } from '../../components/StatusFilter';
import { Icon, InlineIcon } from '../../components/Icon';
import { LoadMoreBar } from '../../components/LoadMoreBar';
import { buildApiFilter, usePaginatedApiRows } from '../../core/searchFilters';
import { buildOrderParam, sortRows, useTableTools, TABLE_PAGE_SIZE } from '../../core/tableView';
import { SortableTh } from '../../components/SortableTh';

const INV_ORDER = { invoiceNo: 'invoice_no', clientName: 'client_name', date: 'date', total: 'total', paid: 'paid', pending: 'pending', status: 'status' };
const INV_ACCESSORS = {
  invoiceNo: (r) => (r.invoiceNo || '').toLowerCase(),
  clientName: (r) => (r.clientName || '').toLowerCase(),
  date: (r) => (r.date ? new Date(r.date).getTime() : 0),
  total: (r) => Number(r.total) || 0,
  paid: (r) => Number(r.paid) || 0,
  pending: (r) => Number(r.pending) || 0,
  status: (r) => invoiceStatus(r),
};


function InvoicesPage() {
  const { invoices, clients, openModal, can, user, visibleClients, payments } = useApp();
  const scopedClients = visibleClients || clients;
  const scopedClientIds = new Set(scopedClients.map(c => c.id));
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [clientId, setClientId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const isTeam = user?.role === "admin" || user?.role === "manager" || user?.role === "employee";
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  // Everything except the status filter — used for both the visible list and the per-status counts.
  const localBaseInvoices = invoices.filter(i => {
    if (user?.role === "employee" && !scopedClientIds.has(i.clientId)) return false;
    if (clientId && i.clientId !== clientId) return false;
    if (search && !((i.invoiceNo||"").toLowerCase().includes(search.toLowerCase()) || (i.clientName||"").toLowerCase().includes(search.toLowerCase()))) return false;
    if (!dateInRange(i.date, from, to)) return false;
    return true;
  });
  const apiFilter = buildApiFilter({
    equals: { ...(clientId ? { company_id: clientId } : {}) },
    from, to,
    dateField: "date",
    search,
    searchFields: ["invoice_no", "client_name"],
    order: buildOrderParam(sortKey, sortDir, INV_ORDER) || "date.desc",
  });
  const { rows: baseInvoicesRaw, hasMore, loadMore, loadingMore } = usePaginatedApiRows("invoices", apiFilter, localBaseInvoices, { enabled: !!apiFilter });
  const baseInvoices = baseInvoicesRaw.map(inv => resolveInvoiceFinancials(inv, payments));
  const visible = baseInvoices.filter(i => filter === "all" ? true : invoiceStatus(i) === filter || (filter === "unpaid" && invoiceStatus(i) === "overdue"));
  const clearFilters = () => { setSearch(""); setClientId(""); setFrom(""); setTo(""); };
  const nonVoid    = visible.filter(i => i.status !== "void"); // #G14 voided invoices don't count toward money totals
  const tTotal     = nonVoid.reduce((s, i) => s + i.total, 0);
  const tPaid      = nonVoid.reduce((s, i) => s + i.paid, 0);
  const tPending   = nonVoid.reduce((s, i) => s + i.pending, 0);

  // #1 Status filter — dropdown instead of a chip row (consistent with Tasks).
  const INV_STATUSES = [
    { id: "all", label: "All" },
    { id: "paid", label: "Paid" },
    { id: "partial", label: "Partial" },
    { id: "unpaid", label: "Unpaid" },
    { id: "void", label: "Void" },
  ];
  const statusOptions = INV_STATUSES.map(s => ({
    id: s.id, label: s.label,
    count: s.id === "all" ? baseInvoices.length : baseInvoices.filter(i => i.status === s.id).length,
  }));

  return (
    <>
      {/* #2 Summary cards — mobile-friendly card grid */}
      <div className="stat-cards cols-3">
        {[["Total Invoiced", INR(tTotal), "var(--ink)"], ["Collected", INR(tPaid), "var(--green)"], ["Outstanding", INR(tPending), "var(--red)"]].map(([l, v, c]) => (
          <div key={l} className="stat-box"><div className="stat-lbl">{l}</div><div className="stat-val" style={{ fontSize: 22, color: c }}>{v}</div></div>
        ))}
      </div>
      <FilterBar
        search={search} setSearch={setSearch} searchPlaceholder="Search invoice # / client…"
        clientId={clientId} setClientId={isTeam ? setClientId : undefined} clients={scopedClients}
        from={from} setFrom={setFrom} to={to} setTo={setTo} onClear={clearFilters} />
      <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
        <StatusFilter value={filter} onChange={setFilter} options={statusOptions} label="Status" />
        {can("invoices","create") && <button className="btn btn-primary" style={{ marginLeft: "auto" }} onClick={() => openModal("create-invoice")}>+ New Invoice</button>}
      </div>
      <InvoicesTable
        invoices={visible}
        hasMore={hasMore}
        loadMore={loadMore}
        loadingMore={loadingMore}
        shownCount={baseInvoices.length}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={toggleSort}
        apiSorted
      />
    </>
  );
}

function InvoicesTable({
  invoices, onPayNow, hasMore, loadMore, loadingMore, shownCount,
  sortKey: controlledSortKey, sortDir: controlledSortDir, onSort, apiSorted = false,
  localPageSize = TABLE_PAGE_SIZE,
}) {
  const { openModal, user } = useApp();
  const isClient = user?.role === "client";
  const isApiPaged = hasMore != null || loadMore != null;
  const local = useTableTools(invoices, {
    pageSize: localPageSize,
    defaultSort: { key: 'date', dir: 'desc' },
    accessors: INV_ACCESSORS,
    apiPaged: apiSorted || isApiPaged,
  });
  const sortKey = controlledSortKey ?? local.sortKey;
  const sortDir = controlledSortDir ?? local.sortDir;
  const toggleSort = onSort ?? local.toggleSort;
  const displayInvoices = (apiSorted || isApiPaged)
    ? (apiSorted ? invoices : sortRows(invoices, sortKey, sortDir, INV_ACCESSORS))
    : local.visible;
  const showLoadMore = isApiPaged ? hasMore : local.hasMore;
  const onLoadMore = isApiPaged ? loadMore : local.loadMore;
  const loadMoreCount = isApiPaged ? shownCount : local.shownCount;
  const statusStyle = { paid:{bg:"#F0FDF4",color:"#16A34A"}, partial:{bg:"#FFFBEB",color:"#B45309"}, unpaid:{bg:"#FEF2F2",color:"#DC2626"}, overdue:{bg:"#FEF2F2",color:"#DC2626"}, void:{bg:"#F3F4F6",color:"#6B7280"} };
  return (
    <div className="card">
      <div className="tbl-freeze-col table-scroll-shell">
      <div className="tbl-wrap tbl-m-invoices">
        <table>
          <thead><tr>
            <SortableTh label="Invoice #" sortKey="invoiceNo" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <SortableTh label="Client" sortKey="clientName" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <SortableTh label="Date" sortKey="date" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <SortableTh label="Total" sortKey="total" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <SortableTh label="Paid" sortKey="paid" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <SortableTh label="Pending" sortKey="pending" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <SortableTh label="Status" sortKey="status" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <th></th>
          </tr></thead>
          <tbody>
            {displayInvoices.map(i => {
              const st = invoiceStatus(i);
              const ss = statusStyle[st] || statusStyle.unpaid;
              return (
                <tr key={i.id}>
                  <td style={{ fontWeight:700, color:"var(--blue)", fontSize:12 }}>{i.invoiceNo}</td>
                  <td style={{ fontSize:12 }}>{i.clientName}</td>
                  <td style={{ fontSize:12, color:"var(--muted)" }}>{i.date}</td>
                  <td style={{ fontWeight:700 }}>{INR(i.total)}</td>
                  <td style={{ color:"var(--green)", fontWeight:600 }}>{INR(i.paid)}</td>
                  <td style={{ color:i.status==="void"?"var(--muted)":i.pending>0?"var(--red)":"var(--muted)", fontWeight:600 }}>
                    {i.status === "void" ? "Cancelled" : INR(i.pending)}
                  </td>
                  <td><span className="badge" style={{ background:ss.bg, color:ss.color }}><span className="badge-dot" style={{ background:ss.color }}/>{st}</span></td>
                  <td><div className="td-actions">
                    {!isClient && (
                      <button className="btn btn-sm" onClick={()=>openModal("view-invoice",{invoice:i})}>View</button>
                    )}
                    {!isClient && i.status!=="paid" && i.status!=="void" && (
                      <button className="btn btn-sm" style={{ background:"var(--green)",color:"#fff",border:"none" }}
                        onClick={()=>openModal("record-payment",{invoiceId:i.id})}>Record Payment</button>
                    )}
                    {isClient && i.status!=="paid" && (
                      <button className="btn btn-sm" style={{ background:"linear-gradient(135deg,#16A34A,#15803D)",color:"#fff",border:"none" }}
                        onClick={()=>openModal("pay-now",{invoiceId:i.id})}><InlineIcon name="card" size={13} gap={5}>Pay Now</InlineIcon></button>
                    )}
                  </div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </div>
      <LoadMoreBar hasMore={showLoadMore} loadingMore={loadingMore} onLoadMore={onLoadMore} count={loadMoreCount} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// TASKS PAGE
// ═══════════════════════════════════════════════════════════════════════
const isTaskOverdue = (t) => !!t.dueDate && new Date(t.dueDate) < new Date() && t.status !== "completed" && t.status !== "cancelled";


export { InvoicesPage, InvoicesTable };
